import { useGetIdentity, useTranslate } from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";

import { formatMxn } from "../quoter/PlanSchedule";
import type { AppRole, OperationSummary } from "../types";
import { SemaphoreBadge, StatusLabel } from "./OperationBadges";
import { getOperationFilterSources } from "./operationRules";
import {
  OPERATION_ADMINISTRATIVE_STATUSES,
  OPERATION_COMMERCIAL_STATUSES,
  OPERATION_SEMAPHORES,
} from "../types";

const toChoices = (prefix: string, values: readonly string[]) =>
  values.map((id) => ({ id, name: `${prefix}.${id}` }));

/**
 * Almikia Epic E4 (TASK-027): "Operaciones" list. Newest first, server-side
 * pagination and filters. Cancelled operations stay in the list (the
 * "Estado comercial" filter narrows to them); nothing is ever hidden or
 * deleted. Row scope is enforced by RLS (asesora: hers, lider: her team's).
 */
export const OperationList = () => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  if (!identity) return null;

  const sources = getOperationFilterSources(identity.role as AppRole);
  const filters = [
    <SearchInput
      key="q"
      source="q"
      alwaysOn
      placeholder={translate("crm.operations.search")}
    />,
    <ReferenceInput
      key="project"
      source="project_id"
      reference="projects"
      label={false}
      alwaysOn
    >
      <SelectInput
        label={false}
        optionText="name"
        emptyText="crm.operations.filters.project"
      />
    </ReferenceInput>,
    <SelectInput
      key="commercial"
      source="commercial_status"
      label={false}
      alwaysOn
      emptyText="crm.operations.filters.commercial_status"
      choices={toChoices(
        "crm.operations.commercial_status",
        OPERATION_COMMERCIAL_STATUSES,
      )}
    />,
    <SelectInput
      key="administrative"
      source="administrative_status"
      label={false}
      alwaysOn
      emptyText="crm.operations.filters.administrative_status"
      choices={toChoices(
        "crm.operations.administrative_status",
        OPERATION_ADMINISTRATIVE_STATUSES,
      )}
    />,
    <SelectInput
      key="semaphore"
      source="semaphore"
      label={false}
      alwaysOn
      emptyText="crm.operations.filters.semaphore"
      choices={toChoices("crm.operations.semaphore", OPERATION_SEMAPHORES)}
    />,
    ...(sources.includes("sales_id")
      ? [
          <ReferenceInput
            key="advisor"
            source="sales_id"
            reference="sales"
            label={false}
            alwaysOn
          >
            <SelectInput
              label={false}
              optionText={(sale: { first_name: string; last_name: string }) =>
                `${sale.first_name} ${sale.last_name}`
              }
              emptyText="crm.operations.filters.advisor"
            />
          </ReferenceInput>,
        ]
      : []),
  ];

  return (
    <List
      resource="operations"
      sort={{ field: "created_at", order: "DESC" }}
      perPage={25}
      filters={filters}
      actions={false}
    >
      <DataTable rowClick="show" bulkActionButtons={false}>
        <DataTable.Col source="id" label="crm.operations.fields.id" />
        {/* Epic EC: a corretaje operation has no project / unit, so its option address stands in. */}
        <DataTable.Col
          source="project_name"
          label="crm.operations.fields.project_or_option"
          disableSort
          render={(record: OperationSummary) =>
            record.project_name ?? record.option_address ?? "—"
          }
        />
        <DataTable.Col
          source="unit_identifier"
          label="crm.operations.fields.unit"
          disableSort
          render={(record: OperationSummary) => record.unit_identifier ?? "—"}
        />
        <DataTable.Col
          source="sales_name"
          label="crm.operations.fields.advisor"
          disableSort
        />
        <DataTable.Col
          source="commercial_status"
          label="crm.operations.fields.commercial_status"
          render={(record: OperationSummary) => (
            <StatusLabel kind="commercial" value={record.commercial_status} />
          )}
        />
        <DataTable.Col
          source="administrative_status"
          label="crm.operations.fields.administrative_status"
          render={(record: OperationSummary) => (
            <StatusLabel
              kind="administrative"
              value={record.administrative_status}
            />
          )}
        />
        <DataTable.Col
          source="semaphore"
          label="crm.operations.fields.semaphore"
          render={(record: OperationSummary) => (
            <SemaphoreBadge value={record.semaphore} />
          )}
        />
        <DataTable.Col
          source="sale_value"
          label="crm.operations.fields.sale_value"
          className="text-right tabular-nums"
          render={(record: OperationSummary) =>
            record.sale_value != null ? formatMxn(record.sale_value) : "—"
          }
        />
        <DataTable.Col
          source="contract_folio"
          label="crm.operations.fields.folio"
        />
      </DataTable>
    </List>
  );
};
